import { supabase } from '@/integrations/supabase/client';

// Function to test direct bucket access with multiple retries
const testBucketAccess = async (bucketName: string, maxRetries = 2) => {
  let retries = 0;
  
  while (retries <= maxRetries) {
    try {
      console.log(`Test d'accès au bucket ${bucketName} (tentative ${retries + 1}/${maxRetries + 1})...`);
      
      // Try to list files in the bucket (limiting to 1 result to optimize)
      const { data, error } = await supabase.storage.from(bucketName).list('', {
        limit: 1
      });
      
      // Add detailed logs to better understand the response
      console.log(`Réponse pour bucket ${bucketName}:`, { data, error });
      
      if (error) {
        if (error.message.includes('bucket') || error.message.includes('permission') || error.message.includes('policy')) {
          console.error(`Problème d'accès au bucket ${bucketName}:`, error.message);
          console.log(`Le bucket ${bucketName} existe mais n'est pas accessible.`);
          return { hasAccess: false, error: error };
        }
        
        // The bucket exists but is empty or another non-fatal error
        console.log(`Le bucket ${bucketName} existe mais est peut-être vide.`);
        return { hasAccess: true, error: null, bucketCreated: false };
      }
      
      // Check if data is null or undefined, or if it's an empty array
      // This check is problematic because an empty response is valid for an empty bucket
      if (!data) {
        console.warn(`Réponse ambiguë pour le bucket ${bucketName}: data est ${data}`);
        // We consider the bucket exists anyway, since the API didn't return an error
        return { hasAccess: true, error: null, bucketCreated: false };
      }
      
      console.log(`Accès au bucket ${bucketName} réussi. Nombre de fichiers: ${data?.length || 0}`);
      return { hasAccess: true, error: null, bucketCreated: false };
    } catch (error) {
      console.error(`Erreur lors du test d'accès au bucket ${bucketName}:`, error);
      
      // If it's the last attempt, report error
      if (retries === maxRetries) {
        console.error(`Échec de toutes les tentatives d'accès au bucket ${bucketName}`);
        return { hasAccess: false, error, bucketCreated: false };
      }
      
      // Otherwise, wait and retry
      retries++;
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }
  
  // We should never get here, but just in case...
  return { hasAccess: false, error: new Error('Échec de toutes les tentatives'), bucketCreated: false };
};

export const setupSupabaseApp = async () => {
  try {
    console.log("Initialisation de l'application Supabase...");
    
    // Wait a moment to ensure all Supabase connections are ready
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // 1. Check if storage buckets exist in Supabase
    const bucketsResult = await supabase.storage.listBuckets();
    
    console.log("Résultat de listBuckets:", bucketsResult);
    
    if (bucketsResult.error) {
      // Instead of failing, just log the error and continue with a warning
      console.error('Erreur lors de la récupération des buckets:', bucketsResult.error.message);
      
      // Try to check buckets by direct access instead
      const escortPhotosAccess = await testBucketAccess('escort-photos');
      const profileImagesAccess = await testBucketAccess('profile-images');
      
      // If we can access at least some buckets, consider it a partial success
      const canAccessSomeBuckets = escortPhotosAccess.hasAccess || profileImagesAccess.hasAccess;
      
      return { 
        success: canAccessSomeBuckets,
        status: canAccessSomeBuckets ? 'warning' : 'error',
        hasAllBuckets: false,
        hasPoliciesIssues: true,
        error: bucketsResult.error,
        message: canAccessSomeBuckets 
          ? `Impossible de lister les buckets mais certains semblent accessibles: ${escortPhotosAccess.hasAccess ? 'escort-photos' : ''} ${profileImagesAccess.hasAccess ? 'profile-images' : ''}` 
          : `Erreur lors de la récupération des buckets Supabase: ${bucketsResult.error.message}`
      };
    }
    
    const buckets = bucketsResult.data || [];
    
    // Check for required buckets
    const requiredBuckets = ['escort-photos', 'profile-images'];
    const existingBucketIds = buckets.map(bucket => bucket.name);
    
    console.log('Buckets trouvés dans Supabase:', existingBucketIds);
    
    // Check which required buckets exist
    const missingBuckets = requiredBuckets.filter(id => !existingBucketIds.includes(id));
    
    // 2. Test direct access to buckets (even if they exist in the list)
    const escortPhotosAccess = await testBucketAccess('escort-photos');
    const profileImagesAccess = await testBucketAccess('profile-images');
    
    // Analyze results and determine status
    let messages = [];
    let status = 'success';
    
    // Check real access to buckets
    const escortPhotosExists = existingBucketIds.includes('escort-photos') || escortPhotosAccess.hasAccess;
    const profileImagesExists = existingBucketIds.includes('profile-images') || profileImagesAccess.hasAccess;
    
    if (!escortPhotosExists) {
      console.warn("Le bucket escort-photos n'existe pas.");
      messages.push("Le bucket escort-photos n'existe pas dans Supabase.");
      status = 'warning';
    } else if (!escortPhotosAccess.hasAccess) {
      console.warn("Le bucket escort-photos existe mais n'est pas accessible.");
      messages.push("Le bucket escort-photos existe mais n'est pas accessible. Vérifiez les politiques d'accès.");
      status = 'warning';
    }
    
    if (!profileImagesExists) {
      console.warn("Le bucket profile-images n'existe pas.");
      messages.push("Le bucket profile-images n'existe pas dans Supabase.");
      status = 'warning';
    } else if (!profileImagesAccess.hasAccess) {
      console.warn("Le bucket profile-images existe mais n'est pas accessible.");
      messages.push("Le bucket profile-images existe mais n'est pas accessible. Vérifiez les politiques d'accès.");
      status = 'warning';
    }
    
    if (messages.length === 0) {
      messages.push("Tous les buckets de stockage sont correctement configurés et accessibles.");
      console.log("Storage buckets initialized successfully");
    }
    
    // We consider it a success even if the buckets have issues
    // The application can work without them, just image storage won't work
    return { 
      success: true,
      status: status,
      hasAllBuckets: escortPhotosExists && profileImagesExists,
      hasPoliciesIssues: !escortPhotosAccess.hasAccess || !profileImagesAccess.hasAccess,
      missingBuckets: missingBuckets,
      message: messages.join(' ')
    };
  } catch (error) {
    console.error("Erreur lors de l'initialisation de l'application Supabase:", error);
    return { 
      success: false, 
      status: 'error',
      hasAllBuckets: false,
      hasPoliciesIssues: true,
      error,
      message: `Erreur lors de l'initialisation de l'application Supabase: ${error instanceof Error ? error.message : String(error)}`
    };
  }
};

export const setupSupabaseProcedures = async () => {
  try {
    console.log("Setting up Supabase stored procedures...");
    
    // First, check if the function already exists using a safer approach
    const { data: functions, error: funcListError } = await supabase
      .from('pg_catalog.pg_proc')
      .select('proname')
      .ilike('proname', 'create_booking%')
      .limit(1);
    
    if (funcListError) {
      console.log("Unable to check existing procedures directly:", funcListError);
      console.log("Will try to execute procedure setup anyway...");
    }
    
    const procedureExists = functions && functions.length > 0;
    
    if (!procedureExists) {
      console.log("Creating create_booking procedure...");
      
      // Use a basic approach to create the procedure
      const { error: createError } = await supabase.rpc(
        'setup_create_booking_procedure',
        {} as any
      );
      
      if (createError) {
        // If this specific error occurs, it's likely that the setup procedure doesn't exist
        if (createError.message.includes('Could not find the function') || 
            createError.code === 'PGRST202') {
          console.log("Setup procedure doesn't exist. This is expected in some environments.");
          return { 
            success: true, 
            message: "Procedures may be set up manually in the database. This is not an error."
          };
        }
        
        console.error('Error creating create_booking procedure:', createError);
        return { success: false, error: createError };
      }
      
      console.log("create_booking procedure created successfully");
    } else {
      console.log("create_booking procedure already exists");
    }
    
    return { success: true };
  } catch (error) {
    console.error("Error setting up Supabase procedures:", error);
    return { success: false, error };
  }
};
